import {
  CdkVirtualScrollViewport,
  ScrollingModule,
  VIRTUAL_SCROLL_STRATEGY
} from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { BtnColors } from '@core/models/colors.model';
import { EntityIconService } from '@core/services/entity-icon/entity-icon.service';
import { ICONS } from '@core/services/icons/icons.service';
import { ModifierKeysPressedService } from '@core/services/modifier-keys-pressed/modifier-keys-pressed.service';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { ContextMenuOption } from '@shared/components/context-menu/context-menu.component';
import {
  CellEditEvent,
  CellNavigationEvent,
  TableCellComponent,
  TableCellDataType
} from '@shared/components/table/table-cell/table-cell.component';
import { UidLabelResolver } from '@shared/components/table/table-cell/uid-label-resolver';
import {
  CellOptions,
  GoToReference,
  RowEvent,
  ShowDetails,
  TableAction,
  TableColumn,
  TableConfig,
  TableRowError
} from '@shared/components/table/table.model';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { ContextMenuService } from '@shared/services/context-menu/context-menu.service';
import { getCellValue } from '@shared/utils/table-cell.utils';

import { MeasuredRowVirtualScrollStrategy } from './measured-row-virtual-scroll.strategy';
import { resolveShiftRange } from './shift-range-selection.util';

type NavDirection = 'right' | 'left' | 'up' | 'down';

/** Row height assumed for the very first render, before any row has been measured. */
const ESTIMATED_ROW_HEIGHT_PX = 40;
/** How close rendered rows may come to the viewport edge before more are rendered. */
const MIN_BUFFER_PX = 600;
/** How far past the viewport to render when topping the buffer up. */
const MAX_BUFFER_PX = 1200;

/**
 * The context-menu service is a root singleton keyed by string, and two tables can share a
 * `tableId` (two detail panes of the same type side by side). Each instance's keys get this
 * suffix so neither overwrites the other's handler or unregisters it on destroy.
 */
let nextInstanceId = 0;

/** Resolves a reference column's uid to a human-readable name; undefined = unresolved/keep uid. */
export type ReferenceNameLookup = (entityType: string, uid: string) => string | undefined;

@Component({
  selector: 'app-table-content',
  imports: [
    CheckboxComponent,
    TableCellComponent,
    TranslatePipe,
    TooltipComponent,
    ScrollingModule
  ],
  templateUrl: './table-content.component.html',
  styleUrl: './table-content.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: () =>
        new MeasuredRowVirtualScrollStrategy(
          ESTIMATED_ROW_HEIGHT_PX,
          MIN_BUFFER_PX,
          MAX_BUFFER_PX
        )
    }
  ],
  host: {
    '(window:keydown)': 'handleKeyDown($event)',
    '[style.--row-stripe]': 'stripeColor()'
  }
})
export class TableContentComponent<T> implements OnInit, OnDestroy {
  data = input<T[]>([]);
  tableId = input.required<string>();
  /**
   * Current selection as a Set of row references. Passed as a signal (not a
   * bound predicate) so this OnPush component re-checks when selection changes
   * upstream — e.g. select-all in the header. The owner emits a new Set on every
   * change (see EntityComponent.selectedItemsSet), so identity comparison works.
   */
  selected = input<Set<T>>(new Set<T>());
  config = input<TableConfig<T>>({});
  tableColumnStyles = input<Record<string, string>>({});
  columns = input<TableColumn<T>[]>([]);
  ownerEntityType = input<string>('');
  actions = input<TableAction<T>[]>([]);
  isThereHiddenColumns = input<boolean>(false);
  errors = input<TableRowError<T>[]>([]);
  pendingRowUids = input<ReadonlySet<string>>(new Set<string>());
  cellOptions = input<CellOptions<T>[]>([]);
  referenceNames = input<ReferenceNameLookup | undefined>(undefined);

  goToReference = output<GoToReference<T>>();
  showDetails = output<ShowDetails<T>>();
  rowEdit = output<RowEvent<T>>();
  listItemClick = output<{ row: T; column: keyof T; item: unknown }>();

  trackByKey = computed(() => this.config().rowId || ('uid' as keyof T));

  /**
   * The row whose cell editor is open, or null when nothing is being edited. Held by
   * reference rather than by index (like `selected`) so sorting, filtering or
   * removing rows moves the highlight with the record instead of leaving it on
   * whatever now sits at that position.
   *
   * Driven by the cell's own edit events rather than by focus: the editor is rendered
   * by the dropdown singleton, outside the row's DOM, so focus legitimately leaves the
   * row for the whole time the user is typing into it.
   */
  protected readonly activeRow = signal<T | null>(null);

  /** Pending clear of {@link activeRow}; see `onCellEditEnd`. */
  private clearActiveRowTimer?: ReturnType<typeof setTimeout>;

  protected readonly ICONS = ICONS;
  protected readonly String = String;

  private readonly contextMenuService = inject(ContextMenuService);
  private readonly translate = inject(TranslateService);
  private readonly entityIconsService = inject(EntityIconService);
  private readonly modifierKeys = inject(ModifierKeysPressedService);

  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  private resizeObserver?: ResizeObserver;
  private refreshScheduled = false;

  // Last row toggled by a plain (non-shift) checkbox click; the fixed start point
  // for a shift-click range. Stays put across shift-clicks, resets on plain clicks,
  // and only anchors a range while it is itself still selected (see resolveShiftRange).
  private anchorRow: T | null = null;

  // Keys the context-menu handlers were registered under, kept rather than re-derived on
  // teardown: they are built from `tableId`, a required input, and reading a required input
  // once the view is destroyed throws NG0950.
  private registeredContextMenuKeys: string[] = [];
  private readonly instanceId = nextInstanceId++;

  constructor() {
    if (this.isRowsEditableInline() && !this.rowEdit) {
      throw new Error('Row edit output must be provided when editable is true');
    }

    // New rows behind the same indices carry their own heights, and no render reports
    // that, so the measurements have to be discarded here.
    effect(() => {
      this.data();
      this.scheduleViewportRefresh();
    });

    // The viewport only recomputes its own size on window resize. When its container
    // resizes without a window event — toggling the visualization panel, dragging the
    // split — its cached height goes stale and it renders rows for the old (smaller)
    // height, covering only part of the page. A narrower container also rewraps the cells,
    // changing every row's height.
    //
    // The scrollport is observed rather than the viewport: the viewport's own height is
    // the content size the scroll strategy sets, so observing it would turn each
    // measurement into another invalidation, round and round.
    afterNextRender(() => {
      const scrollport = this.viewport()?.scrollable?.getElementRef().nativeElement;
      if (!scrollport) return;
      this.resizeObserver = new ResizeObserver(() => this.scheduleViewportRefresh());
      this.resizeObserver.observe(scrollport);
      this.scheduleViewportRefresh();
    });
  }

  /** Coalesce the viewport re-measurements into one pass per animation frame. */
  private scheduleViewportRefresh(): void {
    if (this.refreshScheduled) return;
    this.refreshScheduled = true;
    requestAnimationFrame(() => {
      this.refreshScheduled = false;
      // Re-measures the viewport's own size and, by way of the scroll strategy's
      // onDataLengthChanged, every row's height. Keep both: a resized container renders a
      // different number of rows *and* rewraps the cells inside them, and replaced data
      // puts different rows behind the same indices.
      this.viewport()?.checkViewportSize();
    });
  }

  ngOnInit() {
    this.registeredContextMenuKeys = [
      this.getRowContextMenuKey(),
      this.getEditableRowContextMenuKey()
    ];

    this.contextMenuService.register(this.getRowContextMenuKey(), (target) => {
      const rowId = target.getAttribute('data-row-id');

      if (!rowId) {
        return [];
      }

      const rowIndex = this.extractRowIndexFromDataAttribute(rowId);

      const disabledReason = this.config().editable?.disabledReason;
      const edition: ContextMenuOption = {
        label: this.translate.instant('table.editable.edit_row'),
        icon: ICONS.pencil,
        disabled: !!disabledReason,
        hint: disabledReason,
        action: () => {
          const row = this.data()[rowIndex];
          this.rowEdit.emit({
            row,
            index: rowIndex,
            type: 'askFormEdit'
          });
        }
      };

      const linkedReferences = this.config().linkedReferences ?? [];
      const referenceSubOptions: ContextMenuOption[] = linkedReferences.map((ref) => {
        return {
          label: ref.entityType.toLowerCase(),
          icon: ICONS.link,
          action: () => {
            const row = this.data()[rowIndex];
            this.goToReference.emit({
              row,
              entityType: ref.entityType,
              index: rowIndex,
              type: 'goToReference'
            });
          }
        };
      });
      const references: ContextMenuOption = {
        label: this.translate.instant('table.reference.go_to_reference'),
        action: () => {},
        subOptions: referenceSubOptions
      };

      const availableDetails = (this.config().availableDetails ?? []).map((ref) => {
        return {
          icon: this.entityIconsService.getEntityIcon(ref.entityType),
          label: this.translate.instant('entities.' + ref.entityType.toLowerCase()),
          action: () => {
            const row = this.data()[rowIndex];
            this.showDetails.emit({
              row,
              entityType: ref.entityType,
              index: rowIndex,
              type: 'showDetails'
            });
          }
        };
      });

      const options: ContextMenuOption[] = [];

      if (this.isRowsEditableInNewTab()) {
        options.push(edition);
      }

      if (referenceSubOptions.length > 0) {
        options.push(references);
      }

      options.push(...availableDetails);

      // Parent-supplied, row-specific options (e.g. "Duplicate").
      const customOptions = this.config().rowContextMenuOptions?.(this.data()[rowIndex]);
      if (customOptions?.length) {
        options.push(...customOptions);
      }

      return options;
    });

    this.contextMenuService.register(this.getEditableRowContextMenuKey(), (target) => {
      const rowId = target.getAttribute('data-row-id');

      if (!rowId) {
        return [];
      }

      const rowIndex = this.extractRowIndexFromDataAttribute(rowId);

      return [
        {
          label: this.translate.instant('table.editable.add_row'),
          icon: ICONS.plus,
          action: () => {
            this.rowEdit.emit({
              type: 'add',
              row: {} as T
            });
          }
        },
        {
          label: this.translate.instant('table.editable.delete_row'),
          icon: ICONS.trash,
          action: () => {
            const row = this.data()[rowIndex];
            this.rowEdit.emit({
              row,
              index: rowIndex,
              type: 'delete'
            });
          }
        }
      ];
    });
  }

  ngOnDestroy() {
    // Unregister context menu handlers to prevent memory leaks
    for (const key of this.registeredContextMenuKeys) {
      this.contextMenuService.unregister(key);
    }
    this.resizeObserver?.disconnect();
    clearTimeout(this.clearActiveRowTimer);
  }

  handleKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const td = target.closest('div[role="cell"]');
    const tr = td?.closest('div[role="row"]');

    if (!td || !tr) return;

    const cell = td.querySelector('app-table-cell');

    if (isNil(cell)) {
      return;
    }

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.focusAdjacentCell(cell, 'next');
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.focusAdjacentCell(cell, 'prev');
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusAdjacentCell(cell, 'up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusAdjacentCell(cell, 'down');
        break;
    }
  }

  focusAdjacentCell(current: Element, direction: string) {
    const td = current.closest('div[role="cell"]');
    const tr = current.closest('div[role="row"]');
    if (!td || !tr) return;

    switch (direction) {
      case 'next':
        this.focusCell(td.nextElementSibling);
        break;
      case 'prev':
        this.focusCell(td.previousElementSibling);
        break;
      case 'up': {
        const prevRow = tr.previousElementSibling;
        if (prevRow) {
          const cellIndex = Array.from(tr.children).indexOf(td);
          this.focusCell(prevRow.children[cellIndex]);
        }
        break;
      }
      case 'down': {
        const nextRow = tr.nextElementSibling;
        if (nextRow) {
          const colIndex = Array.from(tr.children).indexOf(td);
          this.focusCell(nextRow.children[colIndex]);
        }
        break;
      }
    }
  }

  private focusCell(td: Element | null) {
    const cell = td?.querySelector<HTMLElement>('[tabindex]');
    if (!isNil(cell)) {
      cell.focus();
    }
  }

  private getContextMenuKey(baseKey: string): string {
    const id = this.tableId();
    return id ? `${baseKey}-${id}-${this.instanceId}` : `${baseKey}-${this.instanceId}`;
  }

  private getRowContextMenuKey(): string {
    return this.getContextMenuKey('table-row');
  }

  private getEditableRowContextMenuKey(): string {
    return this.getContextMenuKey('table-row-editable');
  }

  private isRowsEditableInNewTab(): boolean {
    return this.isEditionEnabled() && this.config().editable!.newTab;
  }

  private isEditionEnabled(): boolean {
    return !!this.config().editable;
  }

  private extractRowIndexFromDataAttribute(rowId: string): number {
    // If tableId exists, rowId will be "tableId-index", otherwise just "index"
    const parts = rowId.split('-');
    return parseInt(parts[parts.length - 1], 10);
  }

  protected isRowsEditableInline(): boolean {
    return this.isEditionEnabled() && this.config().editable!.inline;
  }

  /**
   * Firefox treats any shift+mousedown as "extend the document text selection to here",
   * so a range click drags a blue smear across every cell between the two checkboxes.
   * Cancelling the mousedown default drops the extension; the click that follows still
   * reaches the checkbox, so the row toggles as usual.
   */
  protected onSelectionMouseDown(event: MouseEvent): void {
    if (event.shiftKey) {
      event.preventDefault();
    }
  }

  protected handleSelectionChange(row: T) {
    const selection = this.config().selection;
    const action = selection?.action;
    if (!selection?.enabled || !action) {
      return;
    }

    if (this.modifierKeys.isShiftPressed()) {
      const rowsToToggle = resolveShiftRange(this.data(), this.anchorRow, row, (r) =>
        this.isRowSelected(r)
      );
      if (rowsToToggle !== null) {
        rowsToToggle.forEach((r) => action(r));
        return;
      }
    }

    action(row);
    this.anchorRow = row;
  }

  protected getCellDataType(column: TableColumn<T>): TableCellDataType {
    return column.type ?? 'string';
  }

  /** Resolver for a column's cells, only when it is a name-mode reference column. */
  protected displayLabelForColumn(column: TableColumn<T>): UidLabelResolver | undefined {
    const lookup = this.referenceNames();
    const type = column.referenceEntityType;
    if (!lookup || !type || column.displayMode !== 'name') return undefined;
    return (uid: string) => lookup(type, uid);
  }

  protected getRowDataContext(): string {
    return this.isRowsEditableInline()
      ? this.getEditableRowContextMenuKey()
      : this.getRowContextMenuKey();
  }

  // Error handling methods
  protected isRowHasError(row: T): boolean {
    return this.errors().some((error) => error.row === row);
  }

  private isRowHasLineError(row: T): boolean {
    return this.errors().some((error) => error.row === row && error.column === undefined);
  }

  private isRowHasErrorInOneColumn(row: T): boolean {
    return this.errors().some((error) => error.row === row && error.column !== undefined);
  }

  protected isRowSelected(row: T): boolean {
    return this.selected().has(row);
  }

  /**
   * Click-mode selection turns the whole row into the control. `enabled` is part of it
   * because every other selection path checks it: a config that switches selection off
   * while keeping its handler must stop picking rows.
   */
  protected isClickSelectable(): boolean {
    const selection = this.config().selection;
    return !!selection?.enabled && selection.mode === 'click' && !!selection.action;
  }

  protected selectByRowClick(row: T): void {
    if (!this.isClickSelectable()) {
      return;
    }

    this.config().selection?.action?.(row);
  }

  /**
   * Redefines `--row-stripe` for the whole subtree so `.gx-row-tint.is-striped`, which
   * already resolves its tint through that variable, picks the override up without a
   * per-variant rule. `null` leaves the global token in place.
   */
  protected readonly stripeColor = computed<string | null>(
    () => this.config().stripeColor ?? null
  );

  /**
   * Which rows carry a stripe, by position in `data()`. Without `stripeGroupKey` this is
   * plain index parity; with one, the parity flips on every change of key instead, so a
   * run of rows belonging together reads as one band.
   *
   * Indexed by position rather than keyed by row: the template hands `getRowClasses` the
   * index CDK renders at, and owners mutate their row objects in place, which a
   * reference-keyed map would not survive.
   */
  private readonly stripedRows = computed<boolean[]>(() => {
    const rows = this.data();
    const groupKey = this.config().stripeGroupKey;
    if (isNil(groupKey)) {
      return rows.map((_row, index) => index % 2 === 1);
    }

    let striped = false;
    let currentKey: string | null = null;
    return rows.map((row) => {
      const key = groupKey(row);
      if (isNil(key)) {
        return false;
      }
      if (key !== currentKey) {
        currentKey = key;
        striped = !striped;
      }
      return striped;
    });
  });

  protected getRowClasses(row?: T, index?: number): string {
    // `group` (unnamed) is required so cell-level `group-hover:` states resolve
    // against the row; `transition-colors` drives the Galia hover fade.
    const base = 'group transition-colors';
    const selected =
      !!row &&
      !!this.config().selection?.enabled &&
      !!this.config().selection?.highlightSelected &&
      this.isRowSelected(row);

    // An index-less row is not a data row (the add-row footer): it keeps the
    // plain background treatment, or hover would darken it twice.
    if (!this.config().striped || index === undefined) {
      if (selected) {
        return `${base} bg-accent-interactive hover:bg-accent-interactive-hover`;
      }
      // The row being edited wears the hover background outright, so it stays greyed
      // once the pointer moves off it.
      return this.isEditedRow(row)
        ? `${base} bg-interactive`
        : `${base} hover:bg-interactive`;
    }

    // Striped tables route hover through the tint overlay instead of a row
    // background, so the two never stack into a doubly-darkened row.
    const tinted = [base, 'gx-row-tint'];
    if (this.stripedRows()[index]) {
      tinted.push('is-striped');
    }
    if (this.isEditedRow(row)) {
      tinted.push('is-active');
    }
    if (selected) {
      tinted.push(
        'is-selected',
        'bg-accent-interactive',
        'hover:bg-accent-interactive-hover'
      );
    }
    return tinted.join(' ');
  }

  /** Whether `row` is the one being filled in or edited. */
  private isEditedRow(row?: T): boolean {
    return row !== undefined && row === this.activeRow();
  }

  protected onCellEditStart(row: T): void {
    clearTimeout(this.clearActiveRowTimer);
    this.activeRow.set(row);
  }

  /**
   * Deferred by a macrotask, and cancelled by any editor opening in the meantime:
   * Enter and Tab close one editor and open the next through a `setTimeout` of their
   * own (see `onCellEditNavigate`), so clearing on the spot would blink the row's
   * highlight off between every two cells.
   */
  protected onCellEditEnd(): void {
    clearTimeout(this.clearActiveRowTimer);
    this.clearActiveRowTimer = setTimeout(() => this.activeRow.set(null));
  }

  /**
   * Background of a leading cell pinned by `.table-frozen-cell`. It has to be opaque —
   * the scrolled cells pass underneath it — and it has to follow the row's own selected
   * and hover colours, which a transparent cell would otherwise inherit for free.
   */
  protected getPinnedCellClasses(row: T): string {
    if (this.isRowSelected(row)) {
      return 'bg-accent-interactive group-hover/row:bg-accent-interactive-hover';
    }

    // The pinned cells must stay opaque, or the columns scrolling underneath show
    // through. A striped table paints hover through the row tint overlay, so their
    // own hover background would darken the leading columns twice.
    return this.config().striped
      ? 'bg-elevated'
      : 'bg-elevated group-hover/row:bg-interactive';
  }

  protected getCellOptions(row: T, column: TableColumn<T>): CellOptions<T> | undefined {
    return this.cellOptions().find((opt) => opt.row === row && opt.column === column.key);
  }

  protected getEffectiveEditorConfig(
    row: T,
    column: TableColumn<T>
  ): TableColumn<T>['editorConfig'] {
    if (!column.editorConfigForRow) return column.editorConfig;
    const rowSpecific = column.editorConfigForRow(row);
    if (!rowSpecific) return column.editorConfig;
    return { ...column.editorConfig, ...rowSpecific };
  }

  /** Timing and KPI values of a row whose route is still being recomputed are stale. */
  protected isCellStale(row: T, column: TableColumn<T>): boolean {
    const uid = (row as { uid?: unknown }).uid;
    if (typeof uid !== 'string' || !this.pendingRowUids().has(uid)) return false;
    const key = String(column.key);
    return key.startsWith('kpis') || key.includes('datetime');
  }

  protected isCellHasError(row: T, column: TableColumn<T>): boolean {
    return this.errors().some((error) => error.row === row && error.column === column.key);
  }

  protected handleRowDoubleClick(row: T): void {
    if (this.config().doubleClickAction) {
      this.config().doubleClickAction!(row);
    }
  }

  // Arrow property so `this` stays bound: cdkVirtualFor invokes the trackBy
  // input as a plain TrackByFunction (not in the component context the way
  // @for's `track` expression is), so a plain method would lose `this`.
  protected trackByFn = (index: number, item: T): unknown =>
    item[this.trackByKey()] ?? index;

  protected readonly getCellValue = getCellValue;

  protected onListItemClick(row: T, column: TableColumn<T>, item: unknown): void {
    this.listItemClick.emit({ row, column: column.key, item });
  }

  protected onCellEditCommit(event: CellEditEvent, row: T, column: TableColumn<T>): void {
    const keyStr = String(column.key);

    // Handle nested property paths
    if (keyStr.includes('.')) {
      this.setNestedValue(row, keyStr, event.newValue);
    } else {
      // Simple property update
      row[column.key] = event.newValue as T[keyof T];
    }

    // Emit row edit event
    this.rowEdit.emit({
      type: 'edit',
      index: event.rowIndex ?? 0,
      row,
      column: column.key,
      newValue: event.newValue as T[keyof T]
    });
  }

  /**
   * Excel/Sheets-like navigation: on Enter/Tab while editing, move to the next
   * editable cell (skipping non-editable/greyed ones) and re-open its editor.
   * - Tab / Shift+Tab : next / previous editable cell horizontally (wraps rows).
   * - Enter / Shift+Enter : next / previous editable cell in the same column.
   * Fires on every Enter/Tab, even when the value did not change.
   */
  protected onCellEditNavigate(event: CellNavigationEvent): void {
    const direction: NavDirection =
      event.key === 'Tab' ? (event.shift ? 'left' : 'right') : event.shift ? 'up' : 'down';

    // Defer until the editor dropdown has closed and focus is back on the cell.
    setTimeout(() => {
      const cellElement = document.activeElement?.closest('app-table-cell');
      if (cellElement) {
        this.moveToEditableCellAndEdit(cellElement, direction);
      }
    }, 0);
  }

  private moveToEditableCellAndEdit(
    cellComponentEl: Element,
    direction: NavDirection
  ): void {
    const rowgroup = cellComponentEl.closest('[role="rowgroup"]');
    if (isNil(rowgroup)) return;

    const current = cellComponentEl.querySelector<HTMLElement>(':scope > [tabindex]');
    if (isNil(current)) return;

    const editables = this.getEditableFocusables(rowgroup);
    const target = this.findEditableTarget(editables, current, direction);
    if (isNil(target)) return;

    target.focus();
    // Re-open the editor on the destination cell so the user can type right away.
    target.click();
  }

  /** Focusable inner elements of editable cells (aria-readonly="false"), in document order. */
  private getEditableFocusables(rowgroup: Element): HTMLElement[] {
    return Array.from(
      rowgroup.querySelectorAll<HTMLElement>('div[role="cell"] app-table-cell > [tabindex]')
    ).filter((el) => el.getAttribute('aria-readonly') === 'false');
  }

  /** Index of the cell within its row (includes any leading non-data cells, but consistent across rows). */
  private getCellColumnIndex(focusable: Element): number {
    const td = focusable.closest('div[role="cell"]');
    const row = td?.closest('div[role="row"]');
    if (isNil(td) || isNil(row)) return -1;
    return Array.from(row.children).indexOf(td);
  }

  private findEditableTarget(
    editables: HTMLElement[],
    current: HTMLElement,
    direction: NavDirection
  ): HTMLElement | null {
    const idx = editables.indexOf(current);
    if (idx < 0) return null;

    switch (direction) {
      case 'right':
        return editables[idx + 1] ?? null;
      case 'left':
        return editables[idx - 1] ?? null;
      case 'down': {
        const col = this.getCellColumnIndex(current);
        return (
          editables.slice(idx + 1).find((el) => this.getCellColumnIndex(el) === col) ?? null
        );
      }
      case 'up': {
        const col = this.getCellColumnIndex(current);
        return (
          editables
            .slice(0, idx)
            .reverse()
            .find((el) => this.getCellColumnIndex(el) === col) ?? null
        );
      }
    }
  }

  protected addRow(): void {
    this.rowEdit.emit({
      type: 'add',
      row: {} as T
    });
  }

  protected getActionButtonClasses(color?: BtnColors): string {
    const base =
      'btn btn-icon flex cursor-pointer items-center rounded-sm p-1.5 transition-colors';
    if (color === 'danger') {
      return `${base} hover:text-danger! hover:bg-danger-soft!`;
    }
    return base;
  }

  protected trackActionByLabel<T>(index: number, action: TableAction<T>): string {
    return `${action.label}_${index}`;
  }

  protected getVisibleActions(row: T): TableAction<T>[] {
    return this.actions().filter((action) => !action.visible || action.visible(row));
  }

  protected isActionDisabled(action: TableAction<T>, row: T): boolean {
    return action.disabled ? action.disabled(row) : false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setNestedValue(obj: any, path: string, value: unknown): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;

    let current = obj;
    for (const key of keys) {
      if (!(key in current)) {
        current[key] = {};
      }
      current = current[key];
    }

    current[lastKey] = value;
  }
}
